export type AttributionLoginStatus = 'anonymous' | 'logged_in';

export interface AttributionUrlSnapshot {
    fullUrl: string;
    host: string;
    rootDomain: string;
    path: string;
    query: Record<string, string>;
}

export interface ChannelHistoryItem {
    channel: string;
    firstSeenAt: string;
    lastSeenAt: string;
    landingUrl?: string;
}

export interface AttributionTrailItem {
    visitedAt: string;
    url: AttributionUrlSnapshot;
    referrer?: string;
    channel?: string;
    loginStatus: AttributionLoginStatus;
}

export interface RegisterEntryExperiment {
    key: 'register_popup_v1';
    variant: 'popup' | 'natural';
    exposedAt: string;
    entry: 'register=3';
}

export interface RegistrationAttributionState {
    visitorId: string;
    landing: AttributionUrlSnapshot;
    source: {
        referrer?: string;
        referrerHost?: string;
        referrerRootDomain?: string;
    };
    channelHistory: ChannelHistoryItem[];
    trail: AttributionTrailItem[];
    registerEntryExperiment?: RegisterEntryExperiment;
    capturedAt: string;
    updatedAt: string;
}

export interface RegistrationAttributionPayload extends RegistrationAttributionState {
    channel?: string;
}

export const REGISTRATION_ATTRIBUTION_STORAGE_KEY = 'krene_registration_attribution';
export const KRENE_VISITOR_ID_KEY = 'kreneVisitorId';
export const KRENE_USER_UUID_STORAGE_KEY = 'krene_user_uuid';

const MAX_TRAIL_ITEMS = 100;
const DUPLICATE_CAPTURE_WINDOW_MS = 2000;
const QUERY_WHITELIST = new Set([
    'channel',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'prompt_id',
    'blog_id',
    'kreneVisitorId',
    'register',
]);

export function sanitizeUrlForAttribution(rawUrl: string): AttributionUrlSnapshot {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://www.krene.com';
    const url = new URL(rawUrl, baseUrl);
    const query: Record<string, string> = {};
    for (const [key, value] of url.searchParams.entries()) {
        if (isAllowedQueryKey(key)) query[key] = value.slice(0, 255);
    }
    url.search = '';
    url.hash = '';
    for (const [key, value] of Object.entries(query)) {
        url.searchParams.set(key, value);
    }
    return {
        fullUrl: url.toString(),
        host: url.host,
        rootDomain: rootDomainFromHost(url.hostname),
        path: url.pathname,
        query,
    };
}

export function mergeRegistrationAttribution(
    current: RegistrationAttributionState | null,
    visit: {
        visitorId: string;
        url: string;
        referrer?: string;
        loginStatus: AttributionLoginStatus;
        visitedAt: string;
    },
): RegistrationAttributionState {
    const url = sanitizeUrlForAttribution(visit.url);
    const referrerSnapshot = visit.referrer ? safeSanitizeUrl(visit.referrer) : null;
    const channel = url.query.channel;
    const trailItem: AttributionTrailItem = {
        visitedAt: visit.visitedAt,
        url,
        referrer: referrerSnapshot?.fullUrl,
        channel,
        loginStatus: visit.loginStatus,
    };
    const channelHistory = mergeChannelHistory(current?.channelHistory ?? [], channel, visit.visitedAt, url.fullUrl);
    const trail = appendTrailItem(current?.trail ?? [], trailItem);
    const registerEntryVariant = getRegisterEntryAbVariant(visit.visitorId, visit.url);

    return {
        visitorId: visit.visitorId,
        landing: current?.landing ?? url,
        source: current?.source ?? {
            referrer: referrerSnapshot?.fullUrl,
            referrerHost: referrerSnapshot?.host,
            referrerRootDomain: referrerSnapshot?.rootDomain,
        },
        channelHistory,
        trail,
        registerEntryExperiment: current?.registerEntryExperiment ?? (registerEntryVariant ? {
            key: 'register_popup_v1',
            variant: registerEntryVariant,
            exposedAt: visit.visitedAt,
            entry: 'register=3',
        } : undefined),
        capturedAt: current?.capturedAt ?? visit.visitedAt,
        updatedAt: visit.visitedAt,
    };
}

export function buildRegistrationAttributionPayload(
    state: RegistrationAttributionState | null,
): RegistrationAttributionPayload | null {
    if (!state) return null;
    const lastChannel = state.channelHistory[state.channelHistory.length - 1]?.channel;
    logRegistrationAttribution('payload_build', {
        visitorId: state.visitorId,
        channel: lastChannel || 'none',
        trail: state.trail.length,
        channelHistory: state.channelHistory.length,
    });
    return {
        ...state,
        channel: lastChannel,
    };
}

export function getOrCreateKreneVisitorId(): string {
    const fromUrl = readVisitorIdFromUrl();
    const existing = fromUrl || safeLocalStorageGet(KRENE_VISITOR_ID_KEY);
    if (existing && /^[0-9a-f]{16}$/i.test(existing)) {
        persistVisitorId(existing.toLowerCase());
        return existing.toLowerCase();
    }
    const generated = generateVisitorId();
    persistVisitorId(generated);
    return generated;
}

export function captureRegistrationAttributionVisit(
    loginStatus: AttributionLoginStatus,
    options: {
        url?: string;
        referrer?: string;
        visitedAt?: string;
    } = {},
): RegistrationAttributionState | null {
    if (typeof window === 'undefined') return null;
    const visitorId = getOrCreateKreneVisitorId();
    const current = readRegistrationAttributionState();
    const next = mergeRegistrationAttribution(current, {
        visitorId,
        url: options.url || window.location.href,
        referrer: options.referrer ?? document.referrer ?? '',
        loginStatus,
        visitedAt: options.visitedAt || new Date().toISOString(),
    });
    safeLocalStorageSet(REGISTRATION_ATTRIBUTION_STORAGE_KEY, JSON.stringify(next));
    logRegistrationAttribution('visit_capture', {
        visitorId,
        channel: next.channelHistory[next.channelHistory.length - 1]?.channel || 'none',
        landingHost: next.landing.host || 'none',
        referrerRootDomain: next.source.referrerRootDomain || 'none',
        trail: next.trail.length,
        loginStatus,
    });
    return next;
}

export function readRegistrationAttributionState(): RegistrationAttributionState | null {
    const raw = safeLocalStorageGet(REGISTRATION_ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        return parsed as RegistrationAttributionState;
    } catch {
        return null;
    }
}

export function buildCurrentRegistrationAttributionPayload(): RegistrationAttributionPayload | null {
    return buildRegistrationAttributionPayload(readRegistrationAttributionState());
}

export function clearRegistrationAttribution(): void {
    if (typeof window === 'undefined') return;
    try {
        localStorage.removeItem(REGISTRATION_ATTRIBUTION_STORAGE_KEY);
    } catch {
        // ignore storage failures
    }
    logRegistrationAttribution('cleared');
}

export function setSharedLoginState(loggedIn: boolean, userUuid?: string | null): void {
    if (typeof window === 'undefined') return;
    if (loggedIn && userUuid) {
        safeLocalStorageSet(KRENE_USER_UUID_STORAGE_KEY, userUuid);
    } else if (!loggedIn) {
        try {
            localStorage.removeItem(KRENE_USER_UUID_STORAGE_KEY);
        } catch {
            // ignore storage failures
        }
    }
    logRegistrationAttribution('shared_login_state', {
        loggedIn,
        hasUserUuid: Boolean(userUuid),
        storage: 'localStorage',
    });
}

export function isRegisterIntentFromUrl(): boolean {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    const value = params.get('register') || params.get('auth');
    if (value === '1' || value === 'true' || value === 'register') {
        return true;
    }
    return getRegisterEntryAbVariantFromUrl() === 'popup';
}

export function getRegisterEntryAbVariantFromUrl(): 'popup' | 'natural' | null {
    if (typeof window === 'undefined') return null;
    return getRegisterEntryAbVariant(getOrCreateKreneVisitorId(), window.location.href);
}

export function getRegisterEntryAbVariant(
    visitorId: string,
    rawUrl: string,
): 'popup' | 'natural' | null {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://www.krene.com';
    const url = new URL(rawUrl, baseUrl);
    if ((url.searchParams.get('register') || url.searchParams.get('auth')) !== '3') return null;
    // 按访客 ID 固定分成 5 桶：4 桶自动弹注册，1 桶不自动弹。
    const bucket = Number.parseInt(visitorId.slice(-8), 16) % 5;
    return bucket < 4 ? 'popup' : 'natural';
}

export function attributionQuerySummary(state: RegistrationAttributionState | null): string {
    if (!state) return '';
    const query = state.landing.query || {};
    return Object.entries(query)
        .filter(([key]) => key === 'channel' || key.startsWith('utm_'))
        .map(([key, value]) => `${key}=${value}`)
        .join('&');
}

function mergeChannelHistory(
    history: ChannelHistoryItem[],
    channel: string | undefined,
    seenAt: string,
    landingUrl: string,
): ChannelHistoryItem[] {
    if (!channel) return history;
    const last = history[history.length - 1];
    if (last?.channel === channel) {
        return [...history.slice(0, -1), { ...last, lastSeenAt: seenAt }];
    }
    return [...history, { channel, firstSeenAt: seenAt, lastSeenAt: seenAt, landingUrl }];
}

function appendTrailItem(
    trail: AttributionTrailItem[],
    item: AttributionTrailItem,
): AttributionTrailItem[] {
    const last = trail[trail.length - 1];
    if (last && last.url.fullUrl === item.url.fullUrl && last.loginStatus === item.loginStatus) {
        const lastTime = Date.parse(last.visitedAt);
        const itemTime = Date.parse(item.visitedAt);
        if (
            Number.isFinite(lastTime) &&
            Number.isFinite(itemTime) &&
            Math.abs(itemTime - lastTime) <= DUPLICATE_CAPTURE_WINDOW_MS
        ) {
            return [...trail.slice(0, -1), item].slice(-MAX_TRAIL_ITEMS);
        }
    }
    return [...trail, item].slice(-MAX_TRAIL_ITEMS);
}

function safeSanitizeUrl(rawUrl: string): AttributionUrlSnapshot | null {
    try {
        return sanitizeUrlForAttribution(rawUrl);
    } catch {
        return null;
    }
}

function isAllowedQueryKey(key: string): boolean {
    return QUERY_WHITELIST.has(key) || key.startsWith('utm_');
}

function rootDomainFromHost(hostname: string): string {
    const parts = hostname.toLowerCase().split('.').filter(Boolean);
    if (parts.length <= 2) return parts.join('.');
    const suffix = parts.slice(-2).join('.');
    if (['com.cn', 'net.cn', 'org.cn', 'co.uk', 'com.hk'].includes(suffix)) {
        return parts.slice(-3).join('.');
    }
    return parts.slice(-2).join('.');
}

function readVisitorIdFromUrl(): string | null {
    if (typeof window === 'undefined') return null;
    const value = new URLSearchParams(window.location.search).get(KRENE_VISITOR_ID_KEY);
    return value && /^[0-9a-f]{16}$/i.test(value) ? value : null;
}

function persistVisitorId(visitorId: string): void {
    safeLocalStorageSet(KRENE_VISITOR_ID_KEY, visitorId);
}

function generateVisitorId(): string {
    const bytes = new Uint8Array(8);
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        crypto.getRandomValues(bytes);
    } else {
        for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
    }
    return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function safeLocalStorageGet(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

function safeLocalStorageSet(key: string, value: string): void {
    try {
        localStorage.setItem(key, value);
    } catch {
        // ignore storage failures
    }
}

function logRegistrationAttribution(event: string, data?: Record<string, unknown>): void {
    if (typeof console === 'undefined') return;
    console.info(`[registration-attribution] ${event}`, data || {});
}
